import React, { useState, useMemo, useEffect } from 'react';
import { 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiUser, 
  FiSettings, 
  FiCheck, 
  FiX, 
  FiSearch, 
  FiRefreshCw, 
  FiInfo, 
  FiChevronDown 
} from 'react-icons/fi';
import { useModerators } from '../../../hooks/useModerators';
import { useDebounce } from '../../../hooks/useDebounce';
import './ModeratorManage.css';

// Custom Components
const Button = ({ 
  children, 
  onClick, 
  disabled = false, 
  type = 'default', 
  size = 'medium', 
  icon = null, 
  loading = false,
  danger = false,
  className = '',
  ...props 
}) => {
  const baseClasses = "inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  
  const typeClasses = {
    default: "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-blue-500",
    primary: "bg-blue-600 border border-transparent text-white hover:bg-blue-700 focus:ring-blue-500",
    danger: "bg-red-600 border border-transparent text-white hover:bg-red-700 focus:ring-red-500",
    link: "bg-transparent border-transparent text-blue-600 hover:text-blue-700 focus:ring-blue-500"
  };
  
  const sizeClasses = {
    small: "px-2 py-1 text-sm h-8",
    medium: "px-4 py-2 text-sm h-10",
    large: "px-6 py-3 text-base h-12"
  };
  
  const buttonType = danger ? 'danger' : type;
  
  return (
    <button
      className={`${baseClasses} ${typeClasses[buttonType]} ${sizeClasses[size]} ${className}`}
      onClick={onClick}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : icon}
      {children}
    </button>
  );
};

const Input = ({ 
  placeholder = '', 
  value = '', 
  onChange, 
  prefix = null, 
  suffix = null, 
  size = 'medium',
  disabled = false,
  className = '',
  onClear = null,
  allowClear = false,
  ...props 
}) => {
  const sizeClasses = {
    small: "px-3 py-1 text-sm h-8",
    medium: "px-3 py-2 text-sm h-10", 
    large: "px-4 py-3 text-base h-12"
  };

  return (
    <div className={`relative ${className}`}>
      {prefix && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {prefix}
        </div>
      )}
      <input
        type="text"
        className={`
          block w-full border border-gray-300 rounded-lg bg-white text-gray-900 
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
          disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
          ${prefix ? 'pl-10' : ''} 
          ${suffix || allowClear ? 'pr-10' : ''}
          ${sizeClasses[size]}
        `}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        {...props}
      />
      {(suffix || (allowClear && value)) && (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
          {allowClear && value && (
            <button
              type="button"
              onClick={() => onClear && onClear()}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
          {suffix && !allowClear && suffix}
        </div>
      )}
    </div>
  );
};

const TextArea = ({ 
  placeholder = '', 
  value = '', 
  onChange, 
  rows = 4,
  disabled = false,
  className = '',
  ...props 
}) => {
  return (
    <textarea
      className={`
        block w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
        disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
        resize-none ${className}
      `}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      rows={rows}
      disabled={disabled}
      {...props}
    />
  );
};

const Card = ({ children, className = '', hoverable = false, ...props }) => {
  return (
    <div 
      className={`
        bg-white border border-gray-200 rounded-lg shadow-sm p-6
        ${hoverable ? 'hover:shadow-md transition-shadow cursor-pointer' : ''}
        ${className}
      `}
      {...props}
    >
      {children}
    </div>
  );
};

const Badge = ({ status = 'default', text = '', className = '' }) => {
  const statusClasses = {
    success: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-800',
    warning: 'bg-yellow-100 text-yellow-800',
    default: 'bg-gray-100 text-gray-800'
  };

  const dotClasses = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    default: 'bg-gray-500'
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusClasses[status]} ${className}`}>
      <span className={`w-2 h-2 rounded-full mr-1 ${dotClasses[status]}`}></span>
      {text}
    </span>
  );
};

const Tag = ({ children, color = 'default', className = '' }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800 border-blue-200',
    green: 'bg-green-100 text-green-800 border-green-200',
    orange: 'bg-orange-100 text-orange-800 border-orange-200',
    purple: 'bg-purple-100 text-purple-800 border-purple-200',
    cyan: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    red: 'bg-red-100 text-red-800 border-red-200',
    default: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${colorClasses[color]} ${className}`}>
      {children}
    </span>
  );
};

const Checkbox = ({ checked = false, onChange, children, disabled = false, className = '' }) => {
  return (
    <label className={`flex items-center cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="sr-only"
      />
      <div className={`
        relative w-4 h-4 mr-2 border-2 rounded transition-colors
        ${checked ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300'}
        ${!disabled && 'hover:border-blue-500'}
        focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2
      `}>
        {checked && (
          <FiCheck className="w-4 h-4 text-white" />
        )}
      </div>
      {children && <span className="text-sm text-gray-900">{children}</span>}
    </label>
  );
};

const Select = ({ 
  value, 
  onChange, 
  placeholder = 'Select option', 
  children, 
  disabled = false,
  searchValue = '',
  onSearch = null,
  showSearch = false,
  className = '',
  notFoundContent = 'No data',
  ...props 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(searchValue || '');

  useEffect(() => {
    setSearchTerm(searchValue || '');
  }, [searchValue]);

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    onSearch && onSearch(newValue);
  };

  // Convert children to array and find selected option
  const optionsArray = React.Children.toArray(children);
  const selectedOption = optionsArray.find(child => {
    // Handle both direct option values and nested structures
    const optionValue = child.props?.value !== undefined ? child.props.value : child.key;
    return optionValue === value;
  });

  const handleOptionClick = (optionValue) => {
    onChange && onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        className={`
          w-full px-3 py-2 text-left bg-white border border-gray-300 rounded-lg shadow-sm
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
          ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400'}
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <div className="flex items-center justify-between">
          <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
            {selectedOption ? (
              <div className="flex items-center">
                {/* Render the selected option content */}
                {selectedOption.props.children}
              </div>
            ) : placeholder}
          </span>
          <FiChevronDown className="w-4 h-4" />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
          {showSearch && (
            <div className="p-2">
              <Input
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search..."
                prefix={<FiSearch className="w-4 h-4" />}
                size="small"
              />
            </div>
          )}
          <div className="py-1">
            {optionsArray.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">{notFoundContent}</div>
            ) : (
              optionsArray.map((child, index) => {
                const optionValue = child.props?.value !== undefined ? child.props.value : child.key;
                return (
                  <div
                    key={optionValue || index}
                    className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                    onClick={() => handleOptionClick(optionValue)}
                  >
                    {child.props.children || child}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Modal = ({ 
  open = false, 
  onOk, 
  onCancel, 
  title, 
  children, 
  width = 600,
  confirmLoading = false,
  okText = 'OK',
  cancelText = 'Cancel'
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onCancel}></div>
        
        <div 
          className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:p-6 w-full mx-4 max-h-[90vh] overflow-y-auto"
          style={{ maxWidth: `${width}px` }}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
          
          <div className="mb-6">
            {children}
          </div>
          
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <Button onClick={onCancel} className="w-full sm:w-auto">{cancelText}</Button>
            <Button type="primary" onClick={onOk} loading={confirmLoading} className="w-full sm:w-auto">
              {okText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Avatar = ({ icon, className = '', size = 'default' }) => {
  const sizeClasses = {
    small: 'w-6 h-6',
    default: 'w-8 h-8',
    large: 'w-12 h-12'
  };

  return (
    <div className={`${sizeClasses[size]} bg-blue-500 rounded-full flex items-center justify-center text-white ${className}`}>
      {icon}
    </div>
  );
};

const Tooltip = ({ title, children }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children}
      {showTooltip && (
        <div className="absolute z-10 px-2 py-1 text-xs text-white bg-gray-900 rounded bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-nowrap">
          {title}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

const Divider = ({ orientation = 'center', children }) => {
  if (children) {
    return (
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className={`relative flex ${orientation === 'left' ? 'justify-start' : orientation === 'right' ? 'justify-end' : 'justify-center'}`}>
          <span className="px-3 bg-white text-sm text-gray-500">{children}</span>
        </div>
      </div>
    );
  }

  return <hr className="my-6 border-gray-300" />;
};

const Empty = ({ description = 'No data', image = null, children }) => {
  return (
    <div className="text-center py-12">
      {image && (
        <div className="mb-4 flex justify-center">
          <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-4.5V8.5m0 0V8a2 2 0 00-2-2h-7a2 2 0 00-2 2v.5m9 0h.5a2 2 0 012 2v1a2 2 0 01-2 2H15m0-5.5v.5m0 0v3" />
          </svg>
        </div>
      )}
      <p className="text-gray-500 mb-4">{description}</p>
      {children}
    </div>
  );
};

const Popconfirm = ({ title, description, onConfirm, okText = 'Yes', cancelText = 'No', children }) => {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleConfirm = () => {
    onConfirm();
    setShowConfirm(false);
  };

  return (
    <div className="relative inline-block">
      <div onClick={() => setShowConfirm(true)}>
        {children}
      </div>
      
      {showConfirm && (
        <div className="absolute z-50 w-64 p-3 bg-white border border-gray-200 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2">
          <div className="mb-3">
            <div className="flex items-center mb-2">
              <FiInfo className="w-4 h-4" />
              <span className="ml-2 text-sm font-medium">{title}</span>
            </div>
            {description && <p className="text-xs text-gray-600">{description}</p>}
          </div>
          <div className="flex justify-end space-x-2">
            <Button size="small" onClick={() => setShowConfirm(false)}>{cancelText}</Button>
            <Button size="small" type="primary" onClick={handleConfirm}>{okText}</Button>
          </div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-white"></div>
        </div>
      )}
    </div>
  );
};

const Spin = ({ spinning = false, children, tip = 'Loading...' }) => {
  return (
    <div className="relative">
      {children}
      {spinning && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <div className="text-center">
            <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-sm text-gray-600">{tip}</p>
          </div>
        </div>
      )}
    </div>
  );
};

function ModeratorManage() {
  const {
    moderators,
    availableModules,
    users,
    statistics,
    loading,
    modalLoading,
    addModerator,
    updateModerator,
    removeModerator,
    toggleModeratorStatus,
    refreshData,
    fetchUsers,
    fetchModerator
  } = useModerators();

  const [modalVisible, setModalVisible] = useState(false);
  const [editingModerator, setEditingModerator] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [currentModeratorData, setCurrentModeratorData] = useState(null);
  const [formData, setFormData] = useState({
    account: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Debounce search terms to optimize performance
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const debouncedUserSearchTerm = useDebounce(userSearchTerm, 500);

  // Effect to fetch users when search term changes
  useEffect(() => {
    if (modalVisible) {
      fetchUsers(debouncedUserSearchTerm);
    }
  }, [debouncedUserSearchTerm, modalVisible, fetchUsers]);

  // Filter moderators based on debounced search term
  const filteredModerators = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return moderators;
    }

    const searchLower = debouncedSearchTerm.toLowerCase();
    return moderators.filter(moderator => {
      return (
        moderator.account_username?.toLowerCase().includes(searchLower) ||
        moderator.account_mobile?.toLowerCase().includes(searchLower) ||
        moderator.assigned_by_username?.toLowerCase().includes(searchLower) ||
        moderator.notes?.toLowerCase().includes(searchLower) ||
        moderator.accessible_modules?.some(module => 
          module.toLowerCase().includes(searchLower)
        )
      );
    });
  }, [moderators, debouncedSearchTerm]);

  // Helper function to map module names to API permission field names
  const getPermissionFieldName = (moduleName) => {
    const moduleToPermissionMap = {
      'Courses': 'can_access_courses',
      'Books': 'can_access_books',
      'Form': 'can_access_form',
      'SMS History': 'can_access_sms_history',
      'Pre-Booking': 'can_access_pre_booking',
      'Mentor Activity': 'can_access_mentor_activity',
      'Classes': 'can_access_classes',
      'Exams': 'can_access_exams',
      'Edit Profile': 'can_edit_profile',
      'Admission News': 'can_access_admission_news',
      'Add Routine': 'can_add_routine',
      'Notifications': 'can_access_notifications',
      'Add Result': 'can_add_result'
    };
    
    return moduleToPermissionMap[moduleName] || `can_${moduleName.toLowerCase().replace(/\s+/g, '_')}`;
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.account) {
      errors.account = 'Please select a user';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddModerator = () => {
    setEditingModerator(null);
    setFormData({ account: '', notes: '' });
    // Reset module permissions
    availableModules.forEach(module => {
      setFormData(prev => ({ ...prev, [module.key]: false }));
    });
    setFormErrors({});
    setUserSearchTerm('');
    setModalVisible(true);
    // Fetch initial users when modal opens
    fetchUsers('');
  };

  const handleEditModerator = async (moderator) => {
    setEditingModerator(moderator);
    setUserSearchTerm('');
    setModalVisible(true);
    setFormLoading(true);
    
    try {
      // Fetch complete moderator data from API
      const moderatorData = await fetchModerator(moderator.id);
      
      if (moderatorData) {
        // Store the fetched moderator data for use in the form
        setCurrentModeratorData(moderatorData);
        
        // Prepare form values with the fetched nested data
        const formValues = {
          account: moderatorData.account, // This is the ID from the response
          notes: moderatorData.notes || ''
        };

        // Map the permission flags to form fields
        // The API returns boolean flags like can_access_courses, can_access_books, etc.
        availableModules.forEach(module => {
          // Use the helper function to get the correct API permission field name
          const permissionKey = getPermissionFieldName(module.name);
          formValues[module.key] = moderatorData[permissionKey] || false;
        });

        setFormData(formValues);
      } else {
        // Fallback to existing data if API fetch fails
        const formValues = {
          account: moderator.account,
          notes: moderator.notes
        };

        availableModules.forEach(module => {
          formValues[module.key] = moderator[module.key] || false;
        });

        setFormData(formValues);
      }
    } catch (error) {
      const formValues = {
        account: moderator.account,
        notes: moderator.notes
      };

      availableModules.forEach(module => {
        formValues[module.key] = moderator[module.key] || false;
      });

      setFormData(formValues);
    } finally {
      setFormLoading(false);
    }
    
    // Fetch users when modal opens for editing
    fetchUsers('');
  };

  const handleModalOk = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        account: formData.account,
        notes: formData.notes || ''
      };

      // Add module permissions
      availableModules.forEach(module => {
        payload[module.key] = formData[module.key] || false;
      });

      let success;
      if (editingModerator) {
        success = await updateModerator(editingModerator.id, payload);
      } else {
        success = await addModerator(payload);
      }

      if (success) {
        setModalVisible(false);
        setFormLoading(false);
        setCurrentModeratorData(null);
        setFormData({ account: '', notes: '' });
        setFormErrors({});
      }
    } catch (error) {
      console.error('Error saving moderator:', error);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getModuleColor = (moduleName) => {
    const colors = {
      'Courses': 'blue',
      'Books': 'green',
      'Form': 'orange',
      'SMS History': 'purple',
      'Pre-Booking': 'cyan',
      'Mentor Activity': 'blue',
      'Classes': 'red',
      'Exams': 'red',
      'Edit Profile': 'orange',
      'Admission News': 'green',
      'Add Routine': 'blue',
      'Notifications': 'blue',
      'Add Result': 'green'
    };
    return colors[moduleName] || 'default';
  };

  // Custom Table Component
  const Table = ({ columns, dataSource, loading, pagination = true }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    
    const paginatedData = pagination 
      ? dataSource.slice((currentPage - 1) * pageSize, currentPage * pageSize)
      : dataSource;
    
    const totalPages = Math.ceil(dataSource.length / pageSize);

    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <Spin spinning={true} tip="Loading..." />
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column, index) => (
                <th 
                  key={index}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  style={{ width: column.width }}
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.map((record, rowIndex) => (
              <tr key={record.id || rowIndex} className="hover:bg-gray-50">
                {columns.map((column, colIndex) => (
                  <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {column.render 
                      ? column.render(record[column.dataIndex], record, rowIndex)
                      : record[column.dataIndex]
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        
        {pagination && totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <Button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, dataSource.length)}
                  </span>{' '}
                  of <span className="font-medium">{dataSource.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <Button 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="rounded-l-md"
                  >
                    Previous
                  </Button>
                  {[...Array(totalPages)].map((_, index) => (
                    <Button 
                      key={index + 1}
                      onClick={() => setCurrentPage(index + 1)}
                      type={currentPage === index + 1 ? 'primary' : 'default'}
                      className="rounded-none"
                    >
                      {index + 1}
                    </Button>
                  ))}
                  <Button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="rounded-r-md"
                  >
                    Next
                  </Button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'account_username',
      key: 'user',
      width: '200px',
      render: (text, record) => (
        <div className="flex items-center space-x-3">
          <Avatar icon={<FiUser />} />
          <div>
            <div className="font-medium text-gray-900">{record.account_username}</div>
            <div className="text-sm text-gray-500">{record.account_mobile}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Accessible Modules',
      dataIndex: 'accessible_modules',
      key: 'modules',
      width: '350px',
      render: (modules) => (
        <div className="max-w-xs">
          {modules && modules.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {modules.slice(0, 3).map(module => (
                <Tag key={module} color={getModuleColor(module)}>
                  {module}
                </Tag>
              ))}
              {modules.length > 3 && (
                <Tooltip title={modules.slice(3).join(', ')}>
                  <Tag>+{modules.length - 3} more</Tag>
                </Tooltip>
              )}
            </div>
          ) : (
            <span className="text-gray-500">No modules assigned</span>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'status',
      width: '100px',
      render: (isActive) => (
        <Badge
          status={isActive ? 'success' : 'error'}
          text={isActive ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Assigned By',
      dataIndex: 'assigned_by_username',
      key: 'assigned_by',
      width: '120px',
      render: (text) => text || 'System',
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '120px',
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '150px',
      render: (_, record) => (
        <div className="flex items-center space-x-2">
          <Tooltip title="Edit Moderator">
            <Button
              type="primary"
              icon={<FiEdit className="w-4 h-4" />}
              size="small"
              onClick={() => handleEditModerator(record)}
            />
          </Tooltip>
          <Tooltip title={record.is_active ? "Deactivate" : "Activate"}>
            <Button
              type={record.is_active ? "default" : "primary"}
              icon={record.is_active ? <FiX className="w-4 h-4" /> : <FiCheck className="w-4 h-4" />}
              size="small"
              onClick={() => toggleModeratorStatus(record)}
            />
          </Tooltip>
          <Tooltip title="Remove Moderator">
            <Popconfirm
              title="Are you sure you want to remove this moderator?"
              description="This action cannot be undone."
              onConfirm={() => removeModerator(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                icon={<FiTrash2 className="w-4 h-4" />}
                size="small"
              />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-blue-600 flex items-center">
            <FiSettings className="w-6 h-6" />
            <span className="ml-2">Moderator Management</span>
          </h1>
          <p className="text-gray-600 mt-1">
            Manage organization moderators and their module access permissions
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
          <Input
            placeholder="Search moderators..."
            prefix={<FiSearch className="w-4 h-4" />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-80"
            size="large"
            allowClear
            onClear={() => setSearchTerm('')}
          />
          <Button
            icon={<FiRefreshCw className="w-4 h-4" />}
            onClick={refreshData}
            loading={loading}
            className="w-full sm:w-auto"
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<FiPlus className="w-4 h-4" />}
            size="large"
            onClick={handleAddModerator}
            className="shadow-lg w-full sm:w-auto"
          >
            Add Moderator
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="text-center bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-lg p-6">
          <div className="text-3xl font-bold">{statistics.active}</div>
          <div className="text-blue-100">Active Moderators</div>
        </div>
        <div className="text-center bg-gradient-to-br from-pink-500 to-red-500 text-white rounded-lg p-6">
          <div className="text-3xl font-bold">{statistics.total}</div>
          <div className="text-pink-100">Total Moderators</div>
        </div>
        <div className="text-center bg-gradient-to-br from-blue-400 to-cyan-400 text-white rounded-lg p-6">
          <div className="text-3xl font-bold">{statistics.inactive}</div>
          <div className="text-blue-100">Inactive</div>
        </div>
        <div className="text-center bg-gradient-to-br from-pink-400 to-yellow-400 text-white rounded-lg p-6">
          <div className="text-3xl font-bold">{statistics.totalModules}</div>
          <div className="text-pink-100">Available Modules</div>
        </div>
      </div>

      {/* Main Table */}
      <Card className="shadow-lg p-0">
        {/* Search Results Info */}
        {debouncedSearchTerm && (
          <div className="bg-blue-50 border-b border-blue-200 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
            <div className="flex items-center">
              <FiSearch className="w-4 h-4" />
              <span className="ml-2">
                Found <span className="font-semibold">{filteredModerators.length}</span> moderator(s) 
                matching "<span className="font-semibold">{debouncedSearchTerm}</span>"
              </span>
            </div>
            <Button 
              type="link" 
              size="small" 
              onClick={() => setSearchTerm('')}
            >
              Clear search
            </Button>
          </div>
        )}

        {filteredModerators.length === 0 && !loading ? (
          <div className="p-6">
            <Empty
              description={debouncedSearchTerm ? `No moderators found matching "${debouncedSearchTerm}"` : "No moderators found"}
              image={true}
            >
              {!debouncedSearchTerm && (
                <Button type="primary" onClick={handleAddModerator}>
                  Add First Moderator
                </Button>
              )}
            </Empty>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredModerators}
            loading={loading}
            pagination={true}
          />
        )}
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={
          <div className="flex items-center text-lg">
            <FiUser className="w-5 h-5" />
            <span className="ml-2">
              {editingModerator ? 'Edit Moderator' : 'Add New Moderator'}
            </span>
          </div>
        }
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          setUserSearchTerm('');
          setFormLoading(false);
          setCurrentModeratorData(null);
          setFormData({ account: '', notes: '' });
          setFormErrors({});
        }}
        width={900}
        confirmLoading={modalLoading}
        okText={editingModerator ? 'Update Moderator' : 'Add Moderator'}
      >
        <Spin spinning={formLoading} tip="Loading moderator data...">
          <div className="space-y-6">
            {/* User Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select User *
              </label>
              <Select
                placeholder="Search and select a user..."
                showSearch
                searchValue={userSearchTerm}
                onSearch={setUserSearchTerm}
                value={formData.account}
                onChange={(value) => handleFormChange('account', value)}
                className="w-full"
                disabled={editingModerator} // Can't change user when editing
                notFoundContent={userSearchTerm ? "No users found" : "Type to search users"}
              >
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    <div className="flex items-center space-x-2">
                      <Avatar size="small" icon={<FiUser />} />
                      <span>{user.username}</span>
                      <span className="text-gray-500">({user.mobile})</span>
                    </div>
                  </option>
                ))}
                {/* Show the selected user even if not in the current users list */}
                {editingModerator && currentModeratorData?.account_details && 
                 !users.find(u => u.id === currentModeratorData.account) && (
                  <option key={currentModeratorData.account} value={currentModeratorData.account}>
                    <div className="flex items-center space-x-2">
                      <Avatar size="small" icon={<FiUser />} />
                      <span>{currentModeratorData.account_details.username}</span>
                      <span className="text-gray-500">({currentModeratorData.account_details.mobile})</span>
                    </div>
                  </option>
                )}
              </Select>
              {formErrors.account && (
                <p className="mt-1 text-sm text-red-600">{formErrors.account}</p>
              )}
            </div>

            <Divider orientation="left">
              <div className="flex items-center text-blue-600">
                <FiInfo className="w-4 h-4" />
                <span className="ml-2 font-medium">Module Access Permissions</span>
              </div>
            </Divider>

            {/* Module Permissions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableModules.map(module => (
                <Card key={module.key} hoverable className="p-0">
                  <div className="p-4">
                    <Checkbox
                      checked={formData[module.key] || false}
                      onChange={(e) => handleFormChange(module.key, e.target.checked)}
                    >
                      <div className="ml-2">
                        <div className={`font-medium text-${getModuleColor(module.name)}-600`}>
                          {module.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {module.description}
                        </div>
                      </div>
                    </Checkbox>
                  </div>
                </Card>
              ))}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <TextArea
                rows={4}
                placeholder="Add any notes about this moderator's responsibilities..."
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
              />
            </div>
          </div>
        </Spin>
      </Modal>
    </div>
  );
}

export default ModeratorManage;